import { useState } from "react";
import { Link } from "react-router";
import { STOCK_CATEGORIES, STOCK_CATEGORY_TITLE, STOCK_COLOURS, STOCK_LEVEL_LABEL } from "../../shared/stock";
import type {
  StockCategory,
  StockColour,
  StockItem,
  StockKind,
  StockLevel,
  StockOverview,
  StockRequest,
} from "../../shared/types";
import { Swatch } from "../cleaner/StockPage";
import { Dialog, Empty, Field } from "../components/ui";
import { patch, post } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { useApi } from "../lib/useApi";
import { Check, fmtIsoDate, LoadState, PageHeader, toApiError, usePolling } from "./common";

const LEVEL_BADGE: Record<StockLevel, string> = { out: "danger", low: "warn", collect: "info" };

function RequestLines({ r }: { r: StockRequest }) {
  return (
    <ul className="adm-stock-lines">
      {r.lines.map((l) => (
        <li key={l.itemId} className="adm-stock-line">
          <Swatch colour={l.colour} />
          <span>
            <strong>{l.name}</strong>{" "}
            <span className={`badge ${LEVEL_BADGE[l.level]}`}>{STOCK_LEVEL_LABEL[l.level]}</span>
            {l.detail && <span className="muted small"> · {l.detail}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function OpenRequest({ r, onDone }: { r: StockRequest; onDone: (msg: string) => void }) {
  const [sorting, setSorting] = useState(false);
  return (
    <li>
      <div className="adm-item">
        <div className="main">
          <div className="title">
            <Link to={`/admin/people/${r.userId}`}>{r.userName}</Link> ·{" "}
            <Link to={`/admin/sites/${r.siteId}`}>{r.siteName}</Link>
          </div>
          <div className="meta">
            {fmtDateTime(r.createdAt)}
            {r.source === "friday" && " · Friday stock check"}
          </div>
          <RequestLines r={r} />
          {r.note && <div className="adm-note">“{r.note}”</div>}
        </div>
        <div className="actions">
          <button className="btn primary sm" onClick={() => setSorting(true)}>
            Mark as sorted
          </button>
        </div>
      </div>
      {sorting && (
        <SortDialog
          r={r}
          onClose={() => setSorting(false)}
          onSorted={() => onDone(`Marked ${r.userName}'s request for ${r.siteName} as sorted.`)}
        />
      )}
    </li>
  );
}

function SortDialog({
  r,
  onClose,
  onSorted,
}: {
  r: StockRequest;
  onClose: () => void;
  onSorted: () => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function go() {
    setBusy(true);
    setError(null);
    try {
      await post(`/admin/stock/requests/${r.id}/done`, { note: note.trim() || null });
      onSorted();
      onClose();
    } catch (e) {
      setError(toApiError(e).message);
      setBusy(false);
    }
  }
  return (
    <Dialog title="Mark as sorted" onClose={onClose}>
      <p>
        {r.userName} at {r.siteName}, {fmtDateTime(r.createdAt)}. The cleaner will see it as sorted.
      </p>
      <Field label="Note (optional, office only)">
        <input
          value={note}
          maxLength={500}
          placeholder="e.g. Dropped off Monday"
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      {error && <div className="banner danger">{error}</div>}
      <div className="row">
        <button className="btn primary" onClick={go} disabled={busy}>
          {busy ? "Saving…" : "Mark as sorted"}
        </button>
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}

interface ItemForm {
  name: string;
  detail: string;
  category: StockCategory;
  kind: StockKind;
  colour: StockColour | "";
  active: boolean;
  sort: string;
}

function ItemDialog({
  item,
  onClose,
  onSaved,
}: {
  item: StockItem | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [f, setF] = useState<ItemForm>(
    item
      ? {
          name: item.name,
          detail: item.detail ?? "",
          category: item.category,
          kind: item.kind,
          colour: item.colour ?? "",
          active: item.active,
          sort: String(item.sort),
        }
      : { name: "", detail: "", category: "chemicals", kind: "stock", colour: "", active: true, sort: "" },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof ItemForm>(k: K, v: ItemForm[K]) => setF((p) => ({ ...p, [k]: v }));

  async function save() {
    if (!f.name.trim()) return setError("Give the item a name.");
    const sort = f.sort.trim() === "" ? undefined : Number(f.sort);
    if (sort !== undefined && (!Number.isInteger(sort) || sort < 0))
      return setError("Order must be a whole number.");
    setBusy(true);
    setError(null);
    const bodyJson = {
      name: f.name.trim(),
      detail: f.detail.trim() || null,
      category: f.category,
      kind: f.kind,
      colour: f.colour || null,
      active: f.active,
      ...(sort !== undefined ? { sort } : {}),
    };
    try {
      if (item) await patch(`/admin/stock/items/${item.id}`, bodyJson);
      else await post("/admin/stock/items", bodyJson);
      onSaved(item ? `Saved ${bodyJson.name}.` : `Added ${bodyJson.name}.`);
      onClose();
    } catch (e) {
      setError(toApiError(e).message);
      setBusy(false);
    }
  }

  return (
    <Dialog title={item ? "Edit stock item" : "Add stock item"} onClose={onClose}>
      <Field label="Name cleaners see" hint="Keep it short, e.g. “Black bin bags”.">
        <input value={f.name} maxLength={60} onChange={(e) => set("name", e.target.value)} />
      </Field>
      <Field label="Office description" hint="Only the office sees this, e.g. size, colour and which bins.">
        <input value={f.detail} maxLength={300} onChange={(e) => set("detail", e.target.value)} />
      </Field>
      <Field label="Group">
        <select value={f.category} onChange={(e) => set("category", e.target.value as StockCategory)}>
          {STOCK_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {STOCK_CATEGORY_TITLE[c]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Cleaners report it as">
        <select value={f.kind} onChange={(e) => set("kind", e.target.value as StockKind)}>
          <option value="stock">Running low / run out</option>
          <option value="collection">Needs collecting (e.g. used cloths)</option>
        </select>
      </Field>
      <Field label="Colour dot" hint="Helps cleaners match the bottle or bag.">
        <select value={f.colour} onChange={(e) => set("colour", e.target.value as StockColour | "")}>
          <option value="">None</option>
          {STOCK_COLOURS.map((c) => (
            <option key={c} value={c}>
              {c[0]?.toUpperCase()}
              {c.slice(1)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Order" hint="Lower numbers show first. Leave empty to put a new item last.">
        <input inputMode="numeric" value={f.sort} onChange={(e) => set("sort", e.target.value)} />
      </Field>
      <Check label="Show to cleaners" checked={f.active} onChange={(v) => set("active", v)} />
      {error && <div className="banner danger">{error}</div>}
      <div className="row">
        <button className="btn primary" onClick={save} disabled={busy}>
          {busy ? "Saving…" : item ? "Save" : "Add item"}
        </button>
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}

const CHECK_BADGE = {
  ok: ["ok", "All good"],
  requested: ["info", "Asked for stock"],
} as const;

export function AdminStockPage() {
  const overview = useApi<StockOverview>("/admin/stock");
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState<StockItem | "new" | null>(null);
  usePolling(overview.reload, 60_000);
  const done = (msg: string) => {
    setMessage(msg);
    void overview.reload();
  };

  return (
    <div className="page">
      <PageHeader title="Stock" sub="Top-up requests from cleaners, and the Friday stock check" />
      {message && (
        <div className="banner ok" role="status" style={{ marginBottom: 12 }}>
          <div className="row between">
            <span>{message}</span>
            <button className="btn ghost sm" onClick={() => setMessage(null)} aria-label="Dismiss">
              ✕
            </button>
          </div>
        </div>
      )}
      <LoadState state={overview}>
        {(o) => (
          <>
            <section className="adm-section card">
              <h2>Waiting to be sorted</h2>
              {o.open.length ? (
                <ul className="list">
                  {o.open.map((r) => (
                    <OpenRequest key={r.id} r={r} onDone={done} />
                  ))}
                </ul>
              ) : (
                <Empty>No stock requests waiting.</Empty>
              )}
            </section>

            <div className="grid cols-2 adm-section">
              <section className="card">
                <h2>Friday stock check</h2>
                <p className="muted small">
                  {fmtIsoDate(o.week, { weekday: "long", month: "long" })}. Cleaners are asked in the app on
                  Fridays, while they're on site.
                </p>
                {o.checks.length ? (
                  <ul className="list">
                    {o.checks.map((c) => {
                      const badge = c.answer ? CHECK_BADGE[c.answer] : null;
                      return (
                        <li key={c.userId} className="row between">
                          <span>{c.userName}</span>
                          {badge ? (
                            <span className={`badge ${badge[0]}`}>{badge[1]}</span>
                          ) : (
                            <span className="badge">Not answered</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <Empty>No one is assigned to a site yet.</Empty>
                )}
              </section>

              <section className="card">
                <h2>Recently sorted</h2>
                {o.recent.length ? (
                  <ul className="list">
                    {o.recent.map((r) => (
                      <li key={r.id}>
                        <div className="title">
                          {r.userName} · {r.siteName}{" "}
                          <span className={`badge ${r.status === "done" ? "ok" : ""}`}>
                            {r.status === "done" ? "Sorted" : "Cancelled by cleaner"}
                          </span>
                        </div>
                        <div className="meta muted small">
                          Asked {fmtDateTime(r.createdAt)}
                          {r.doneAt && ` · sorted ${fmtDateTime(r.doneAt)}`}
                          {r.doneByName && ` by ${r.doneByName}`}
                        </div>
                        <div className="muted small">
                          {r.lines
                            .map((l) => `${l.name} (${STOCK_LEVEL_LABEL[l.level].toLowerCase()})`)
                            .join(", ")}
                        </div>
                        {r.officeNote && <div className="adm-note">{r.officeNote}</div>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Empty>Nothing in the last 30 days.</Empty>
                )}
              </section>
            </div>

            <section className="adm-section card">
              <div className="row between">
                <h2 style={{ margin: 0 }}>Stock list</h2>
                <button className="btn sm" onClick={() => setEditing("new")}>
                  Add item
                </button>
              </div>
              <p className="muted small">
                Cleaners see only the name and colour dot. The description is for the office.
              </p>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Cleaners see</th>
                      <th>Office description</th>
                      <th>Group</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {o.items.map((i) => (
                      <tr key={i.id} className={i.active ? "" : "muted"}>
                        <td>
                          <span className="row" style={{ gap: 8, flexWrap: "nowrap" }}>
                            <Swatch colour={i.colour} />
                            <strong>{i.name}</strong>
                          </span>
                          {!i.active && <span className="badge">Hidden</span>}
                          {i.kind === "collection" && <span className="badge info">Collection</span>}
                        </td>
                        <td>{i.detail ?? <span className="muted">—</span>}</td>
                        <td className="small">{STOCK_CATEGORY_TITLE[i.category]}</td>
                        <td>
                          <button className="btn ghost sm" onClick={() => setEditing(i)}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </LoadState>
      {editing && (
        <ItemDialog
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={done}
        />
      )}
    </div>
  );
}
